import { inject } from '@angular/core';

import { BehaviorSubject, combineLatest, map, mergeMap, Observable, Observer, of, shareReplay, take, tap } from 'rxjs';

import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../Dialogs/models/confirm-dialog.model';
import { BaseModel } from '../models/base.model';
import { AllResourceAbstractService } from './all-resource-abstract.service';

// F -> Frontend model
// B -> Backend api model
// L -> Frontend List model
// BL -> Backend List api model
export abstract class CurrentResourceAbstractService<F extends BaseModel<F, B, IdType>, B, L, BL, IdType = number>
    extends AllResourceAbstractService<F, B, L, BL, IdType> {
    protected currentResourceSubject: BehaviorSubject<F> = new BehaviorSubject<F>(null);
    protected cachedResource: F;
    protected currentResourceModified$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

    protected loadingSubject = new BehaviorSubject<boolean>(false);
    loading$: Observable<boolean> = this.loadingSubject.asObservable();


    currentResource$: Observable<F> = this.currentResourceSubject.asObservable().pipe(
        tap(resource => {
            if (resource?.id) {
                this.cachedResource = resource.clone();
            } else {
                this.cachedResource = null;
            }
        }),
        shareReplay(1)
    );

    resourceDirty$: Observable<boolean> = combineLatest([this.currentResource$, this.currentResourceModified$]).pipe(
        map(([resource, x]) => {
            if (!resource) {
                return false;
            }

            return !resource.equals(this.cachedResource);
        }),
        shareReplay(1)
    );

    protected localizationService = inject(LocalizationService);
    protected coreDialogService = inject(CoreDialogService);

    constructor() {
        super();
        this.subscriptions.push(
            this.actionCompleted$.subscribe(res => {
                if (res.refreshResource) {
                    this.currentResourceSubject.next(res.resource as F);
                }

                if (res.resetResource) {
                    this.currentResourceSubject.next(null);
                }
            })
        );
    }

    /**
     * Should only be called to set a new focused resource.
     * On CRUD actions;
     * - refresh the current resource by setting `ResourceAction.refreshResource` to true
     * - reset the current resource by setting `ResourceAction.resetResource` to true
     * If custom CRUD action are needed, implement this.actionCompletedSubject.next(action)
     */
    setCurrentResource(resource: F): void {
        this.currentResourceSubject.next(resource);
    }

    resetToCachedResource() {
        this.setCurrentResource(this.cachedResource?.clone() ?? null);
    }

    /**
     * Used to trigger an update calculation for {@link resourceDirty$}
     * Since currentResource stores an object (pass by reference), updates to the object won't be detected
     * and therefore this method should be called to ensure the dirty state is updated.
     */
    currentResourceModified(): void {
        this.currentResourceModified$.next();
    }

    canDeactivate$(): Observable<boolean> {
        return this.resourceDirty$.pipe(
            mergeMap(dirty => {
                if (!dirty) {
                    return of(true);
                }

                return new Observable<boolean>(
                    (observer: Observer<boolean>) => {
                        const confirmConfig: ConfirmDialogModel = {
                            inputs: {
                                id: 'showExitDialog',
                                heading: this.localizationService.transform('DeactivateGuard.heading'),
                                messageHtml: this.localizationService.transform('DeactivateGuard.message'),
                                okLabel: this.localizationService.transform('Core.button.confirm')
                            },
                            outputs: {
                                dialogClose: (confirmed: boolean) => {
                                    observer.next(confirmed);
                                    observer.complete();

                                    if (confirmed) {
                                        this.resetToCachedResource();
                                    }
                                }
                            }
                        };

                        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
                    }
                );
            }),
            take(1)
        );
    }
}
